import { ArrowLeft, Mail, Phone, StickyNote, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/Avatar.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { fetchLeadById } from "../services/leadService.js";
import { getCurrentUser } from "../services/authService.js";
import { fetchFields } from "../services/dynamicFieldService.js";
import { fetchUsersByIds } from "../services/teamService.js";
import { getFieldDisplayValue } from "../utils/fieldMapping.js";
import AddNoteModal from "./AddNotes.jsx";
import { DeleteLeadModal } from "./DeleteLeadModel.jsx";
import EditLeadModal from "./EditLeadModal.jsx";

export default function LeadDetails() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assignUser, setAssignUser] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteComment, setNoteComment] = useState("");

  const [activeLeadId, setActiveLeadId] = useState(null);
  const [agentNames, setAgentNames] = useState({});
  const [currentStage, setCurrentStage] = useState("new");
  const [dynamicFields, setDynamicFields] = useState([]);
  const [userRole, setUserRole] = useState(null);

  const loadLeadData = async () => {
    setLoading(true);
    setError("");
    const res = await fetchLeadById(leadId);

    if (res?.success) {
      setLead(res.lead);

      const comments = Array.isArray(res.lead.comment) ? res.lead.comment : [];
      const agentIds = [
        ...new Set(comments.map((c) => c.commented_by).filter(Boolean)),
      ];
      if (agentIds.length > 0) {
        const agents = await fetchUsersByIds(agentIds);
        const nameMap = agents.reduce((acc, agent) => {
          acc[agent.id] = agent.name;
          return acc;
        }, {});
        setAgentNames(nameMap);
      }

      if (res.lead.agent_assigned) {
        const assignedUser = await fetchUsersByIds([res.lead.agent_assigned]);
        setAssignUser(assignedUser[0]?.name);
      }
    } else {
      setError(res?.error || "Failed to load lead");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLeadData();
  }, [leadId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchFields();
        setDynamicFields(res.data || []);
      } catch {
        setDynamicFields([]);
      }
    })();
    getCurrentUser().then((data) => {
      if (data?.user?.user_role) setUserRole(data.user.user_role);
    }).catch(() => {});
  }, []);

  const handleAddNote = (e, leadId) => {
    e.stopPropagation();
    setActiveLeadId(leadId);
    setIsNoteModalOpen(true);
  };

  const getDynamicFieldValue = (field) => {
    return getFieldDisplayValue(lead, field.field_label, field.options);
  };

  if (loading) {
    return (
      <MobileLayout showNavigation={false}>
        <div className="p-4 text-sm">Loading lead...</div>
      </MobileLayout>
    );
  }

  if (error || !lead) {
    return (
      <MobileLayout showNavigation={false}>
        <div className="p-4 text-destructive">{error || "Lead not found"}</div>
      </MobileLayout>
    );
  }

  const stageMap = {
    call_back: "Call Back",
    meeting: "Meeting Setup",
    quotation_created: "Quotation Created",
    quotation_send: "Quotation Send",
    quotation_revised: "Quotation Revised",
    won: "Won",
    lost: "Lost",
    new: "New",
  };

  const leadComments = Array.isArray(lead.comment) ? lead.comment : [];

  const toTitleCase = (str) => {
    if (str === "webhook" || str === "system") return str.charAt(0).toUpperCase() + str.slice(1);
    const name = agentNames[str];
    if (!name) return "Unknown User";
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <MobileLayout showNavigation={false}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Lead Details</h1>
        </div>

        {/* Lead Avatar */}
        <Card className="bg-transparent border-none shadow-none mt-0">
          <CardContent className="flex gap-2 mt-0 transition-shadow bg-white border-2 shadow-sm cursor-pointer text-start border-blue-100/50 rounded-xl">
            <div className="relative inline-block">
              <Avatar className="w-16 h-16">
                <AvatarImage
                  src={lead.avatar || "/placeholder.svg"}
                  alt={lead.name || "User"}
                  className="!rounded-lg"
                />
                <AvatarFallback className="text-2xl font-bold !rounded-lg">
                  {(lead?.name?.[0] || "L").toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            <div className="flex flex-col justify-center">
              <h2 className="text-lg font-bold capitalize">{lead.name}</h2>
              <p className="text-xs text-muted-foreground">
                {stageMap[lead.deal_status] || lead.deal_status || "New"}
              </p>
              <p className="text-xs text-muted-foreground">
                Created{" "}
                {lead.created_at
                  ? new Date(lead.created_at).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })
                  : "—"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="pt-2 pb-0">
            <CardTitle className="text-base font-bold">
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div>
              <Label>Email</Label>
              <Input
                value={lead.email || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={lead.phone_number || lead.phone || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={lead.address || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Fields */}
        {dynamicFields.length > 0 && (
          <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
            <CardHeader className="pt-2 pb-0">
              <CardTitle className="text-base font-bold">
                Lead Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {dynamicFields.map((field) => (
                <div key={field.id}>
                  <Label>{field.field_label}</Label>
                  <Input
                    value={getDynamicFieldValue(field)}
                    disabled
                    className="h-8 text-xs"
                  />
                </div>
              ))}
              <div>
                <Label>Lead Stage</Label>
                <Input
                  value={stageMap[lead.deal_status] || "—"}
                  disabled
                  className="h-8 text-xs"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assigned To */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="py-2">
            <CardTitle className="text-base font-bold">Assigned To</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={assignUser ? assignUser : "Unassigned"}
              disabled
              className="h-8 text-xs"
            />
          </CardContent>
        </Card>

        {/* Comments Section */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="py-2">
            <CardTitle className="text-base font-bold">
              Comments & Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {leadComments.length === 0 ? (
              <p className="text-xs text-muted-foreground">No comments yet.</p>
            ) : (
              leadComments.map((c, i) => (
                <div
                  key={i}
                  className="p-2 text-xs bg-muted/40 rounded-lg space-y-1"
                >
                  <div className="flex justify-between w-full">
                    <p className="font-semibold">
                      {toTitleCase(c.commented_by)}
                    </p>
                    {c.contact_channel ? (
                      <p className="bg-blue-300 text-white py-0.5 px-2 rounded-md text-[10px]">
                        {c.contact_channel}
                      </p>
                    ) : null}
                  </div>
                  <p className="whitespace-pre-wrap break-words overflow-hidden">{c.comment_text}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.comment_at
                      ? new Date(c.comment_at).toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata",
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : "—"}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="py-2">
            <CardTitle className="text-base font-bold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 mb-1">
              <a
                href={`tel:${lead.phone_number}`}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-blue-600 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                title="Call"
              >
                <Phone size={14} />
              </a>
              <a
                href={`mailto:${lead.email}`}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-red-600 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                title="Send Email"
              >
                <Mail size={14} />
              </a>
              <button
                onClick={(e) => handleAddNote(e, lead.id)}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-400 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                title="Add Note"
              >
                <StickyNote size={12} />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Edit & Delete */}
        <div className="flex gap-2">
          <Button
            className="flex-1 h-8 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white"
            onClick={() => setIsEditModalOpen(true)}
          >
            Edit Lead
          </Button>
          {(userRole === "business_admin" || userRole === "admin") && (
            <Button
              className="flex-1 h-8 text-xs rounded-full bg-red-700/90 text-white"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <Trash /> Delete
            </Button>
          )}
        </div>
      </div>

      <AddNoteModal
        isNoteModalOpen={isNoteModalOpen}
        setIsNoteModalOpen={setIsNoteModalOpen}
        noteComment={noteComment}
        setNoteComment={setNoteComment}
        leadId={activeLeadId}
        onCommentAdded={loadLeadData}
        currentStage={currentStage}
        setCurrentStage={setCurrentStage}
      />

      <EditLeadModal
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        lead={lead}
        onLeadUpdated={loadLeadData}
      />

      <DeleteLeadModal
        leadId={leadId}
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
      />
    </MobileLayout>
  );
}
